import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Users, Clock, Star, Dice6, Timer, Target, Trophy, Bookmark } from 'lucide-react';

const RandomPicker = ({ isOpen, onClose, games, onSelectGame }) => {
  const [currentGame, setCurrentGame] = useState(null);
  const [selectedGame, setSelectedGame] = useState(null);

  // Memoize weighted games array - only recalculate when games list changes
  const weightedGames = useMemo(() => {
    const weighted = [];
    games.forEach(game => {
      const baseWeight = game.weight || 1;
      // Add the game multiple times based on its base weight
      for (let i = 0; i < baseWeight; i++) {
        weighted.push(game);
      }
    });
    return weighted;
  }, [games]);

  useEffect(() => {
    if (isOpen && games.length > 0) {
      // PERFORMANCE: Select game immediately when modal opens
      const finalGame = getWeightedRandomGame();
      setSelectedGame(finalGame);
      setCurrentGame(finalGame);
    } else if (!isOpen) {
      // Reset when modal closes
      setSelectedGame(null);
      setCurrentGame(null);
    }
  }, [isOpen, games, weightedGames]);

  const getWeightedRandomGame = () => {
    // Use pre-computed weighted array
    return weightedGames[Math.floor(Math.random() * weightedGames.length)];
  };

  const handleSelectGame = () => {
    if (selectedGame) {
      onSelectGame(selectedGame);
      onClose();
    }
  };

  const handlePickAgain = () => {
    // PERFORMANCE: Instant re-selection
    const finalGame = getWeightedRandomGame();
    setSelectedGame(finalGame);
    setCurrentGame(finalGame);
  };

  const gameToShow = selectedGame || currentGame;

  if (!gameToShow) return null;

  const getComplexityDots = (complexity) => {
    const dots = [];
    const maxDots = 5;
    const filledDots = Math.round(complexity);
    
    for (let i = 1; i <= maxDots; i++) {
      const isFilled = i <= filledDots;
      dots.push(
        <div
          key={i}
          className={`w-2 h-2 rounded-full ${
            isFilled 
              ? complexity <= 2 
                ? 'bg-emerald-500 dark:bg-emerald-400'
                : complexity <= 3 
                ? 'bg-amber-500 dark:bg-amber-400'
                : 'bg-red-500 dark:bg-red-400'
              : 'bg-gray-300 dark:bg-gray-600'
          }`}
        />
      );
    }
    return dots;
  };

  const getStatusInfo = () => {
    if (gameToShow.owned) return { 
      text: 'Owned', 
      color: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-700', 
      icon: Trophy 
    };
    if (gameToShow.wishlist) return { 
      text: 'Wishlist', 
      color: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700', 
      icon: Bookmark 
    };
    if (gameToShow.try) return { 
      text: 'Want to Try', 
      color: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700', 
      icon: Target 
    };
    return { 
      text: 'Owned', 
      color: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-700', 
      icon: Trophy 
    };
  };

  const statusInfo = getStatusInfo();
  const StatusIcon = statusInfo.icon;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg w-full h-[600px] bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-center text-xl text-gray-900 dark:text-white">
            🎯 Tonight you should play
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 flex flex-col justify-center space-y-6 min-h-0">
          {/* Game Card */}
          <Card className="bg-gray-50 dark:bg-gray-900/50 border-gray-200 dark:border-gray-700">
            <CardContent className="p-6 space-y-4 h-[400px] overflow-hidden flex flex-col">
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <h3 className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                    {gameToShow.name}
                  </h3>
                  {gameToShow.try && (
                    <Target size={20} className="text-amber-500 fill-current" />
                  )}
                </div>
                
                {/* Status Badge */}
                <div className="flex justify-center mb-3">
                  <Badge className={`${statusInfo.color} text-sm font-medium border`}>
                    {StatusIcon && <StatusIcon size={14} className="mr-1" />}
                    {statusInfo.text}
                  </Badge>
                </div>
                
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  {gameToShow.description}
                </p>
              </div>

              {/* Game Stats */}
              <div className="grid grid-cols-3 gap-4 py-4 border-t border-b border-gray-200 dark:border-gray-700">
                <div className="text-center">
                  <div className="flex items-center justify-center text-gray-500 dark:text-gray-400 mb-2">
                    <Users size={16} />
                  </div>
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    {gameToShow.players.min}–{gameToShow.players.max}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Players</div>
                </div>
                
                <div className="text-center">
                  <div className="flex items-center justify-center text-gray-500 dark:text-gray-400 mb-2">
                    <Timer size={16} />
                  </div>
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    {gameToShow.playtime}m
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Playtime</div>
                </div>

                <div className="text-center">
                  <div className="flex items-center justify-center gap-0.5 mb-2">
                    {getComplexityDots(gameToShow.complexity)}
                  </div>
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    {Math.floor(gameToShow.complexity * 10) / 10}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Complexity</div>
                </div>
              </div>

              {/* Tags */}
              <div className="flex flex-wrap justify-center gap-1.5">
                {gameToShow.tags.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase())).slice(0, 4).map((tag, index) => (
                  <Badge 
                    key={index}
                    variant="secondary" 
                    className="text-xs bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-700"
                  >
                    {tag}
                  </Badge>
                ))}
              </div>

              {/* Rating */}
              {gameToShow.rating && (
                <div className="flex items-center justify-center gap-1">
                  <Star size={16} className="text-orange-400 fill-current" />
                  <span className="text-lg font-medium text-gray-900 dark:text-white">{gameToShow.rating}</span>
                </div>
              )}

              {gameToShow.isCoOp && (
                <div className="text-center">
                  <Badge className="bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-700">
                    🤝 Cooperative Game
                  </Badge>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={handlePickAgain}
              className="flex-1 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-orange-50 dark:hover:bg-orange-900/20 hover:border-orange-300 dark:hover:border-orange-600 hover:text-orange-600 dark:hover:text-orange-400"
            >
              <Dice6 className="mr-2" size={16} />
              Pick Again
            </Button>
            <Button
              onClick={handleSelectGame}
              className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
            >
              🎮 Play This Game
            </Button>
          </div>

          {games.length > 1 && (
            <div className="text-center space-y-1">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Choosing from {games.length} games matching your filters
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RandomPicker;